import React, {useEffect, useState, useCallback} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Text,
  TouchableOpacity,
  View,
  RefreshControl,
  Image,
  StyleSheet,
  Modal,
  TouchableWithoutFeedback,
  BackHandler,
} from 'react-native';
import {
  useNavigation,
  useFocusEffect,
  useRoute,
} from '@react-navigation/native';
import Header from '../../components/Header/Header';
import {styles} from './styles';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import {AppRoutes} from '../../navigation/Routes.types';
import ProductsService from '../../services/ProductsService/ProductsService';
import ContainersService from '../../services/ContainersService/ContainersService';
import {Colors} from '../../constants/Colors';
import {useAuthStore} from '../../store/AuthStore';
import ExchangeProductsService from '../../services/ExchangeProductsService/ExchangeProductsService';

const reasons = [
  {
    value: 0,
    reason: 'Embalagem com avaria na entrega',
  },
  {
    value: 1,
    reason: 'Divergência da nota na entrega',
  },
  {
    value: 2,
    reason: 'Produto com alteração',
  },
  {
    value: 3,
    reason: 'Outro',
  },
];

const getUnitByReason = (reason: string) => {
  const boxReasons = [
    'Embalagem com avaria na entrega',
    'Divergência da nota na entrega',
  ];
  const bottleReasons = ['Produto com alteração', 'Outro'];

  if (boxReasons.includes(reason)) return 'caixas';
  if (bottleReasons.includes(reason)) return 'Garrafas/latas';
  return '';
};

const RequestEquipmentList = () => {
  const navigation = useNavigation<any>();
  const {goBack} = useNavigation();
  const {navigate} = useNavigation<any>();
  const [loadingItem, setLoadingItem] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [expandedProductIndex, setExpandedProductIndex] = useState<
    number | null
  >(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const route = useRoute();

  useEffect(() => {
    const backAction = () => {
      if (modalVisible) {
        setModalVisible(false);
        return true;
      }
      return false;
    };

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      backAction,
    );

    return () => backHandler.remove();
  }, [modalVisible]);

  const handleNavigation = () => {
    navigate(AppRoutes.MAIN_TAB_SCREEN, {
      screen: AppRoutes.ORDERS,
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const {data: response} =
        await ExchangeProductsService.RequestExchangeProductList();
      setData(response.data);
    } catch (e) {
      console.log('erro ao buscar lista de pedidos', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const updateRequest = async (id: number) => {
    setLoadingItem(id);
    try {
      const result =
        await ExchangeProductsService.UpdateExchangeProductRequestStatus(id, {
          status_id: 3,
        });
      fetchData();
    } catch (e) {
      console.log('erro ao atualizar o status', e);
    } finally {
      setLoadingItem(null);
    }
  };

  const toggleProductDetails = (index: number) => {
    setExpandedProductIndex(expandedProductIndex === index ? null : index);
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchData();
    });

    return unsubscribe;
  }, [navigation]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const renderItem = ({item}: {item: any}) => (
    <View style={styles.itemContainer}>
      <Text style={styles.itemText}>Pedido: {item.order}</Text>
      <Text style={styles.itemText}>
        Status:{' '}
        {item.status.status_name.charAt(0).toUpperCase() +
          item.status.status_name.slice(1)}
      </Text>
      <Text style={styles.itemText}>
        Criado em: {new Date(item.created_at).toLocaleDateString()}
      </Text>
      <Text style={styles.itemText}>Produtos:</Text>

      {item.data &&
        item.data.map((product: any, index: number) => (
          <View key={product.sku} style={styles.productContainer}>
            <TouchableOpacity onPress={() => toggleProductDetails(index)}>
              <Text style={styles.productName}>
                SKU: {parseInt(product.sku)}
              </Text>
            </TouchableOpacity>

            {expandedProductIndex === index && (
              <View style={styles.productDetails}>
                <Text style={styles.productText}>
                  Razão da Troca: {product.reason}
                </Text>
                {!!product?.description && (
                  <Text style={styles.productText}>
                    Descrição: {product.description}
                  </Text>
                )}
                <Text style={styles.productText}>
                  Quantidade: {product.quantity}{' '}
                  {getUnitByReason(product.reason)}
                </Text>

                {product.image && (
                  <TouchableOpacity
                    style={{alignItems: 'center', marginVertical: 10}}
                    onPress={() => {
                      setSelectedImage(product.image);
                      setModalVisible(true);
                    }}>
                    <Image
                      source={{uri: product.image}}
                      style={{width: 150, height: 150}}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        ))}

      {![2, 3].includes(item.status_id) && (
        <ButtonComponent
          outlined
          title={'Cancelar Solicitação'}
          onPress={() => updateRequest(item.id)}
          isLoading={loadingItem === item.id}
        />
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.containerHeader}>
        <SearchHeader onPress={goBack} title="Pedidos de Troca de Produto" />
        <Text style={styles.description}>
          Aqui você pode acompanhar seus pedidos de troca de produto de forma
          fácil e rápida!
        </Text>
      </View>
      {loading && (
        <ActivityIndicator size="large" color={Colors.RedPallete.red} />
      )}
      {error && <Text style={styles.errorText}>{error}</Text>}
      {!error && !loading && (
        <FlatList
          data={data}
          renderItem={renderItem}
          keyExtractor={item => item.id.toString()}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}

      <View style={styles.containerFooter}>
        <ButtonComponent
          title="Ver pedidos para solicitar troca"
          onPress={handleNavigation}
        />
      </View>

      <Modal visible={modalVisible} transparent={true} animationType="fade">
        <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
          <View
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.8)',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {selectedImage && (
              <Image
                source={{uri: selectedImage}}
                style={{width: '90%', height: '60%'}}
                resizeMode="contain"
              />
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

export default RequestEquipmentList;
